import { renderAIWidget } from '@euler/app/components/ai/AIContentRenderer';
import {
  MarkdownRenderer,
  parseContent,
} from '@euler/app/components/message/markdown/MarkdownRenderer';
import { WidgetRenderProps } from '@euler/app/components/message/types';
import { ChartWidthContext } from '@euler/app/components/message/widgets/ChartWidget';
import { CoachingSetupCardView } from '@euler/app/flows/coaching/cards/CoachingSetupCardView';
import {
  ApdmBuildCardKind,
  ApdmBuildStep,
  parseApdmPlanCardWidget,
  parseApdmPlanStepWidget,
} from '@euler/model/apdm';
import { ReactNode, memo } from 'react';
import { CampaignControlCard } from '../cards/CampaignControlCard';
import { CouponCampaignCard } from '../cards/CouponCampaignCard';
import { CouponChoiceCard } from '../cards/CouponChoiceCard';
import { ImportChatCard } from '../cards/ImportChatCard';
import { PlanAudienceCard } from '../cards/PlanAudienceCard';
import { PlanCampaignCard } from '../cards/PlanCampaignCard';
import { PlanCouponChoiceCard } from '../cards/PlanCouponChoiceCard';
import { PlanMessagesCard } from '../cards/PlanMessagesCard';
import { PlanStepCard } from '../cards/PlanStepCard';
import { PlanTimelineLine } from '../cards/PlanTimelineLine';
import { PlanUploadCard } from '../cards/PlanUploadCard';
import { CardLoading } from '../components/ChatCard';

/**
 * A reply in reading order: each run of prose is a bubble, and the lines
 * and cards stand between them where they happened. Follow-ups are pulled out for the chips under the
 * latest reply; a handoff left in an old reply has nothing to offer now
 * that every turn is routed.
 */
export type AiReplySegment =
  | { kind: 'text'; content: string }
  | { kind: 'widget'; widget: string; data: unknown };

export interface AiReplyParts {
  segments: AiReplySegment[];
  suggestions: string[];
  /** All the prose, for copying the reply. */
  text: string;
}

const kHidden = new Set(['suggested_questions', 'ai_bundle_handoff']);

export function replyParts(content: string): AiReplyParts {
  const segments: AiReplySegment[] = [];
  let suggestions: string[] = [];
  const pushText = (text: string) => {
    const last = segments[segments.length - 1];
    if (last?.kind === 'text') last.content += text;
    else segments.push({ kind: 'text', content: text });
  };
  for (const block of parseContent(content)) {
    if (block.type === 'text') pushText(block.content);
    else if (block.type === 'code')
      pushText(`\n\`\`\`${block.language}\n${block.content}\n\`\`\`\n`);
    else if (block.widget === 'suggested_questions') {
      if (Array.isArray(block.data))
        suggestions = block.data.filter(
          (value: unknown): value is string => typeof value === 'string',
        );
    } else if (
      !kHidden.has(block.widget) &&
      !(
        block.widget === '_loading' &&
        kHidden.has((block.data as { tag?: string } | null)?.tag ?? '')
      )
    )
      segments.push({ kind: 'widget', widget: block.widget, data: block.data });
  }
  const visible = segments.filter(
    segment => segment.kind === 'widget' || segment.content.trim(),
  );
  return {
    segments: visible,
    suggestions,
    text: visible
      .flatMap(segment => (segment.kind === 'text' ? [segment.content] : []))
      .map(text => text.trim())
      .join('\n\n')
      .trim(),
  };
}

const kCardSteps: Record<ApdmBuildCardKind, ApdmBuildStep> = {
  [ApdmBuildCardKind.Audience]: ApdmBuildStep.Who,
  [ApdmBuildCardKind.CouponChoice]: ApdmBuildStep.Which,
  [ApdmBuildCardKind.Messages]: ApdmBuildStep.Messages,
  [ApdmBuildCardKind.Campaign]: ApdmBuildStep.Launch,
  [ApdmBuildCardKind.Upload]: ApdmBuildStep.Who,
};

/** The build step a reply's question or card asks about, if any. */
export function stepOfWidget(
  segment: Extract<AiReplySegment, { kind: 'widget' }>,
): ApdmBuildStep | null {
  if (segment.widget === 'apdm_plan_step')
    return parseApdmPlanStepWidget(segment.data)?.step ?? null;
  if (segment.widget !== 'apdm_plan_card') return null;
  const card = parseApdmPlanCardWidget(segment.data);
  return card ? kCardSteps[card] : null;
}

/** The build steps a reply asks about, in its order. */
export function replySteps(parts: AiReplyParts): ApdmBuildStep[] {
  return parts.segments.flatMap(segment => {
    const step = segment.kind === 'widget' ? stepOfWidget(segment) : null;
    return step ? [step] : [];
  });
}

/**
 * A build step's question or card, which carries the step's answers and its
 * ways to adjust. The final campaign card is not one: what to change there
 * is open-ended.
 */
export function isStepAnswerWidget(
  segment: Extract<AiReplySegment, { kind: 'widget' }>,
): boolean {
  const step = stepOfWidget(segment);
  return step != null && step !== ApdmBuildStep.Launch;
}

const kLoadingLabels: Record<string, string> = {
  apdm_campaign_preview: '正在准备活动预览…',
  apdm_coupon_choice: '正在列出优惠券活动…',
  apdm_coupon_campaign: '正在整理优惠券活动…',
  apdm_import_mapping: '正在读取表格…',
  bi_visualization: '正在生成图表…',
  bi_dashboard_choice: '正在列出看板…',
  bi_dashboard_changes: '正在准备看板修改…',
  apdm_plan_card: '正在准备…',
  coaching_setup_card: '正在准备练习…',
};

function renderWidget(
  widget: string,
  data: unknown,
  props: WidgetRenderProps,
): ReactNode | null {
  switch (widget) {
    case 'apdm_campaign_control':
      return <CampaignControlCard data={data} streaming={props.streaming} />;
    case 'apdm_plan_progress':
    case 'apdm_plan_settled':
      return <PlanTimelineLine widget={widget} data={data} />;
    case 'apdm_plan_step':
      return <PlanStepCard data={data} streaming={props.streaming} />;
    case 'apdm_plan_card': {
      const card = parseApdmPlanCardWidget(data);
      const Card =
        card === ApdmBuildCardKind.Audience
          ? PlanAudienceCard
          : card === ApdmBuildCardKind.CouponChoice
          ? PlanCouponChoiceCard
          : card === ApdmBuildCardKind.Messages
          ? PlanMessagesCard
          : card === ApdmBuildCardKind.Campaign
          ? PlanCampaignCard
          : card === ApdmBuildCardKind.Upload
          ? PlanUploadCard
          : null;
      return Card ? (
        <Card messageId={props.messageId} streaming={props.streaming} />
      ) : null;
    }
    case 'apdm_import_mapping':
      return <ImportChatCard data={data} streaming={props.streaming} />;
    case 'apdm_coupon_choice':
      return <CouponChoiceCard data={data} streaming={props.streaming} />;
    case 'apdm_coupon_campaign':
      return <CouponCampaignCard data={data} streaming={props.streaming} />;
    case 'coaching_setup_card':
      return <CoachingSetupCardView data={data} />;
    case '_loading': {
      const tag = (data as { tag?: string } | null)?.tag ?? '';
      if (kLoadingLabels[tag])
        return <CardLoading label={kLoadingLabels[tag]} />;
      return renderAIWidget(widget, data, props);
    }
    default:
      return renderAIWidget(widget, data, props);
  }
}

export const ReplyText = memo(({ content }: { content: string }) => (
  <MarkdownRenderer content={content.trim()} />
));

export const ReplyWidget = memo(
  ({
    widget,
    data,
    messageId,
    blockIndex,
    streaming,
    live,
    width,
  }: {
    widget: string;
    data: unknown;
    messageId: string;
    blockIndex: number;
    streaming: boolean;
    /** Streamed in this visit, so its client actions may run once. */
    live: boolean;
    width: number;
  }) => (
    <ChartWidthContext.Provider value={width}>
      {renderWidget(widget, data, {
        messageId,
        blockIndex,
        streaming,
        autoExecute: live,
      })}
    </ChartWidthContext.Provider>
  ),
);
